import clsx from 'clsx';
import Button from 'components/Qds/Button';
import Link from 'components/ui/Link';
import { useAnalytics } from 'hooks/useAnalytics';
import RichText from './RichText';
import type { IBlockHeroCtaFields } from 'types/contentful/generated';
import type { Document } from '@contentful/rich-text-types';

type Props = IBlockHeroCtaFields & {
  sidebar?: boolean;
  descriptionContent?: Document;
};

const HeroCTA = ({
  title,
  description,
  descriptionContent,
  backgroundColor,
  buttonText,
  buttonAnchor,
  backgroundImageDeskop,
  backgroundImagemobile,
  theme,
  sidebar = false,
  isExternalUrl,
  blockId,
}: Props): JSX.Element => {
  const styleClasses = {
    container: `max-w-screen-xl mx-auto ${
      !backgroundImageDeskop ? 'py-8 px-3' : ''
    } md:px-4 relative ${
      backgroundImageDeskop ? 'flex flex-col justify-center' : ''
    }`,
    h1: `${
      theme === 'Light' ? 'text-white' : 'text-primary'
    } text-3xl md:text-5xl mb-6`,
    p: `${theme === 'Light' ? 'text-white' : 'text-primary'} mb-1 last:mb-6`,
  };

  const { trackEvent } = useAnalytics();
  const hasImage = backgroundImagemobile && backgroundImageDeskop;

  const styles = {
    wrapper: {
      background: backgroundColor.match('#')
        ? backgroundColor
        : `#${backgroundColor}`,
    },
  };

  const scrollToBlock = (): void => {
    trackEvent({
      category: 'CTA',
      action: 'CTA|Click',
      label: `CTA|Block: Hero CTA|${blockId}|${buttonText}`,
    });

    const anchor = document.getElementById(buttonAnchor);
    if (!anchor) {
      return;
    }

    const anchorOffsetTop = anchor.offsetTop;
    const headerHeight = document.querySelector('header')?.offsetHeight;
    window.scrollTo({
      // eslint-disable-next-line
      // @ts-ignore
      top: anchorOffsetTop - headerHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mb-6" style={styles.wrapper}>
      <div className={styleClasses.container}>
        {hasImage && (
          <picture>
            <source
              media="(max-width: 768px)"
              srcSet={
                backgroundImagemobile.fields?.file.url ||
                // eslint-disable-next-line
                // @ts-ignore
                backgroundImagemobile.url
              }
            />
            <img
              src={
                backgroundImageDeskop.fields?.file.url ||
                // eslint-disable-next-line
                // @ts-ignore
                backgroundImageDeskop.url
              }
              alt={
                backgroundImageDeskop.fields?.description ||
                // eslint-disable-next-line
                // @ts-ignore
                backgroundImageDeskop.description
              }
            />
          </picture>
        )}
        <div className={clsx(hasImage && 'absolute', 'px-3 md:px-0')}>
          <div className="md:grid md:grid-cols-6 md:gap-x-5">
            <div
              className={clsx('md:col-span-4', !sidebar && 'md:col-start-2')}
            >
              <h1 className={styleClasses.h1}>{title}</h1>
              <RichText
                fields={descriptionContent || description}
                config={{
                  paragraph: styleClasses.p,
                }}
              />

              {isExternalUrl ? (
                <Link href={buttonAnchor} passHref>
                  <Button
                    isLink
                    onClick={() =>
                      trackEvent({
                        category: 'CTA',
                        action: 'CTA|Click',
                        label: `CTA|Block: Hero CTA|${blockId}|${buttonText}`,
                      })
                    }
                  >
                    {buttonText}
                  </Button>
                </Link>
              ) : (
                <Button onClick={scrollToBlock}>{buttonText}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroCTA;
